import { useCallback, useEffect, useMemo, useState, type Dispatch } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { useTranslation } from 'react-i18next';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode } from '@lexical/list';
import { $createHeadingNode, $isHeadingNode, $isQuoteNode, type HeadingTagType } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import { $isTableSelection } from '@lexical/table';
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import { styled } from '@mui/material/styles';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  type LexicalEditor,
} from 'lexical';

import { InsertTableDialog } from '../../context';
import { useModal } from '../../hooks';
import { ReactComponent as RedoSVG } from '../../images/icons/arrow-clockwise.svg';
import { ReactComponent as UndoSVG } from '../../images/icons/arrow-counterclockwise.svg';
import { ReactComponent as DisableFormatSVG } from '../../images/icons/disable-format.svg';
import { ReactComponent as LinkSVG } from '../../images/icons/link.svg';
import { ReactComponent as NumberListSVG } from '../../images/icons/list-ol.svg';
import { ReactComponent as BulletListSVG } from '../../images/icons/list-ul.svg';
import { ReactComponent as TableSVG } from '../../images/icons/table.svg';
import { ReactComponent as ParagraphSVG } from '../../images/icons/text-paragraph.svg';
import { ReactComponent as BoldSVG } from '../../images/icons/type-bold.svg';
import { ReactComponent as H1SVG } from '../../images/icons/type-h1.svg';
import { ReactComponent as H2SVG } from '../../images/icons/type-h2.svg';
import { ReactComponent as ItalicSVG } from '../../images/icons/type-italic.svg';
import { ReactComponent as SubScriptSVG } from '../../images/icons/type-subscript.svg';
import { ReactComponent as SuperScriptSVG } from '../../images/icons/type-superscript.svg';
import { ReactComponent as UnderlineSVG } from '../../images/icons/type-underline.svg';
import { LexicalDropDown, LexicalDropDownItem } from '../../ui';
import { getSelectedNode } from '../../utils/getSelectedNode';
import { sanitizeUrl } from '../../utils/url';

const IS_APPLE: boolean = /(Mac|iPod|iPhone|iPad)/i.test(navigator.userAgent);

const StyledToolbar = styled('div')(({ theme }) => ({
  display: 'flex',
  marginBottom: '1px',
  background: '#fff',
  padding: '4px',
  borderTopLeftRadius: '10px',
  borderTopRightRadius: '10px',
  verticalAlign: 'middle',
  overflow: 'auto',
  minHeight: '48px',
  position: 'sticky',
  top: '0',
  zIndex: '2',
  alignItems: 'center',
  gap: '8px',
  'button.toolbar-item': {
    border: '0',
    display: 'flex',
    background: 'none',
    borderRadius: '10px',
    padding: '0',
    cursor: 'pointer',
    verticalAlign: 'middle',
    flexShrink: '0',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.palette.neutral[700],
  },
  'button.toolbar-item:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  'button.toolbar-item.spaced': {
    marginRight: '2px',
  },
  'button.toolbar-item:disabled .icon, button.toolbar-item:disabled .text, button.toolbar-item:disabled i.format, button.toolbar-item:disabled .chevron-down':
    {
      opacity: '0.2',
    },
  'button.toolbar-item.active': {
    backgroundColor: 'rgba(223, 232, 250, 0.3)',
  },
  'button.toolbar-item.active i': {
    opacity: '1',
  },
  '.toolbar-item:hover:not([disabled])': {
    backgroundColor: '#eee',
  },
  'button.toolbar-item.font-family .text': {
    display: 'block',
    maxWidth: '40px',
  },
  '.toolbar .toolbar-item .text': {
    display: 'flex',
    lineHeight: '20px',
    verticalAlign: 'middle',
    fontSize: '14px',
    color: '#777',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    height: '20px',
    textAlign: 'left',
    paddingRight: '10px',
  },
  '.toolbar-item .icon': {
    display: 'flex',
    width: '20px',
    height: '20px',
    userSelect: 'none',
    marginRight: '8px',
    lineHeight: '16px',
    backgroundSize: 'contain',
  },
  '.divider': {
    width: '1px',
    backgroundColor: '#eee',
    margin: '0 4px',
  },
  'button.item.dropdown-item-active': {
    backgroundColor: 'rgba(223, 232, 250, 0.3)',
  },
}));

const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
};

function dropDownActiveClass(active: boolean) {
  if (active) {
    return 'active dropdown-item-active';
  } else {
    return '';
  }
}

const BlockFormatDropDown = ({
  editor,
  blockType,
  disabled = false,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  editor: LexicalEditor;
  // eslint-disable-next-line react/boolean-prop-naming
  disabled?: boolean;
}): JSX.Element => {
  const { t } = useTranslation('text-editor');

  const getBlockTypeLabels = (): Partial<Record<keyof typeof blockTypeToBlockName, string>> => ({
    h1: t('blockTypes.heading'),
    h2: t('blockTypes.subheading'),
    paragraph: t('blockTypes.paragraph'),
  });

  const blockTypeLabels = getBlockTypeLabels();

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  return (
    <LexicalDropDown disabled={disabled} buttonLabel={blockTypeLabels[blockType] || blockTypeLabels.paragraph}>
      <LexicalDropDownItem isActive={blockType === 'h1'} onClick={() => formatHeading('h1')}>
        <H1SVG />
        <span>{t('blockTypes.heading')}</span>
      </LexicalDropDownItem>
      <LexicalDropDownItem isActive={blockType === 'h2'} onClick={() => formatHeading('h2')}>
        <H2SVG />
        <span>{t('blockTypes.subheading')}</span>
      </LexicalDropDownItem>
      <LexicalDropDownItem isActive={blockType === 'paragraph'} onClick={formatParagraph}>
        <ParagraphSVG />
        <span>{t('blockTypes.paragraph')}</span>
      </LexicalDropDownItem>
    </LexicalDropDown>
  );
};

export const ToolbarPlugin = ({ setIsLinkEditMode }: { setIsLinkEditMode: Dispatch<boolean> }): JSX.Element => {
  const { t } = useTranslation('text-editor');
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('paragraph');
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [modal, showModal] = useModal();

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, e => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));

      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
        }
      }
    }
  }, [activeEditor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    activeEditor.getEditorState().read(() => {
      $updateToolbar();
    });
  }, [activeEditor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener(editable => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      activeEditor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        payload => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      activeEditor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        payload => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
    );
  }, [$updateToolbar, activeEditor, editor]);

  useEffect(() => {
    return activeEditor.registerCommand(
      KEY_MODIFIER_COMMAND,
      payload => {
        const event: KeyboardEvent = payload;
        const { code, ctrlKey, metaKey } = event;

        if (code === 'KeyK' && (ctrlKey || metaKey)) {
          event.preventDefault();
          let url: string | null;
          if (!isLink) {
            setIsLinkEditMode(true);
            url = sanitizeUrl('https://');
          } else {
            setIsLinkEditMode(false);
            url = null;
          }
          return activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          if ($isTextNode(node)) {
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }
            const extractedTextNode = extractedNodes[0];
            if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
              textNode = extractedTextNode;
            }

            if (textNode.__style !== '') {
              textNode.setStyle('');
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
            }
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [activeEditor]);

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
    } else {
      setIsLinkEditMode(false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const isBlockTypeLabelsEditable = useMemo(() => ['paragraph', 'number', 'bullet'].includes(blockType), [blockType]);

  return (
    <StyledToolbar>
      <button
        disabled={!canUndo || !isEditable}
        onClick={() => {
          activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? t('toolbar.undo') + ' (⌘Z)' : t('toolbar.undo') + ' (Ctrl+Z)'}
        type="button"
        className="toolbar-item spaced"
      >
        <UndoSVG />
      </button>
      <button
        disabled={!canRedo || !isEditable}
        onClick={() => {
          activeEditor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? t('toolbar.redo') + ' (⌘Y)' : t('toolbar.redo') + ' (Ctrl+Y)'}
        type="button"
        className="toolbar-item"
      >
        <RedoSVG />
      </button>
      {blockType in blockTypeToBlockName && activeEditor === editor && (
        <BlockFormatDropDown disabled={!isEditable} blockType={blockType} editor={activeEditor} />
      )}
      <button
        disabled={!isEditable || !isBlockTypeLabelsEditable}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
        title={IS_APPLE ? t('toolbar.bold') + ' (⌘B)' : t('toolbar.bold') + ' (Ctrl+B)'}
        type="button"
      >
        <BoldSVG />
      </button>
      <button
        disabled={!isEditable || !isBlockTypeLabelsEditable}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
        title={IS_APPLE ? t('toolbar.italic') + ' (⌘I)' : t('toolbar.italic') + ' (Ctrl+I)'}
        type="button"
      >
        <ItalicSVG />
      </button>
      <button
        disabled={!isEditable || !isBlockTypeLabelsEditable}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
        className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
        title={IS_APPLE ? t('toolbar.underline') + ' (⌘U)' : t('toolbar.underline') + ' (Ctrl+U)'}
        type="button"
      >
        <UnderlineSVG />
      </button>
      <button
        disabled={!isBlockTypeLabelsEditable}
        className={'toolbar-item spaced ' + dropDownActiveClass(blockType === 'number')}
        onClick={formatNumberedList}
        title={t('toolbar.numberedList')}
        type="button"
      >
        <NumberListSVG />
      </button>
      <button
        disabled={!isBlockTypeLabelsEditable}
        className={'toolbar-item spaced ' + dropDownActiveClass(blockType === 'bullet')}
        onClick={formatBulletList}
        title={t('toolbar.bulletList')}
        type="button"
      >
        <BulletListSVG />
      </button>
      <button
        disabled={!isBlockTypeLabelsEditable}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
        }}
        className={'toolbar-item spaced ' + dropDownActiveClass(isSubscript)}
        title={t('toolbar.subscript')}
        type="button"
      >
        <SubScriptSVG />
      </button>
      <button
        disabled={!isBlockTypeLabelsEditable}
        onClick={() => {
          activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
        }}
        className={'toolbar-item spaced ' + dropDownActiveClass(isSuperscript)}
        title={t('toolbar.superscript')}
        type="button"
      >
        <SuperScriptSVG />
      </button>
      <button
        disabled={!isEditable || !isBlockTypeLabelsEditable}
        onClick={insertLink}
        className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
        title={t('toolbar.insertLink')}
        type="button"
      >
        <LinkSVG />
      </button>
      <button
        disabled={!isBlockTypeLabelsEditable}
        onClick={() => {
          showModal(t('modals.insertTable'), onClose => (
            <InsertTableDialog activeEditor={activeEditor} onClose={onClose} />
          ));
        }}
        className="toolbar-item spaced "
        type="button"
        title={t('toolbar.insertTable')}
      >
        <TableSVG />
      </button>
      <button
        disabled={!isBlockTypeLabelsEditable}
        onClick={clearFormatting}
        className="toolbar-item spaced"
        title={t('toolbar.clearFormatting')}
        type="button"
      >
        <DisableFormatSVG />
      </button>
      {modal}
    </StyledToolbar>
  );
};
